import { Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { useLanguage } from '../../contexts/LanguageContext'
import { Button } from '../ui/button'
import { Magnetic, Tilt, burstFromEvent } from '../celebration'
import ShowcaseSlideshow from './ShowcaseSlideshow'

type HeroProps = {
  onSeeExamples: () => void
}

// Full-viewport hero: candy celebration blobs behind a word-by-word headline
// pop, with the floating (and tilting) example-invitation slideshow
// (ShowcaseSlideshow — shared with the login page), ringed by bobbing bubble
// tags.
const Hero = ({ onSeeExamples }: HeroProps) => {
  const { language } = useLanguage()
  const navigate = useNavigate()
  const isKa = language === 'ka'
  const reduce = useReducedMotion()

  const fadeUp = (delay: number) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay, ease: 'easeOut' as const },
  })

  // Identical copy to before, split into words for the mockup's staggered
  // headline entrance. The key word carries the juicy gradient.
  const words: Array<{ text: string; grad?: boolean }> = isKa
    ? [
        { text: 'დაივიწყე' },
        { text: 'ღონისძიების' },
        { text: 'დაგეგმვის' },
        { text: 'სირთულეები', grad: true },
      ]
    : [
        { text: 'Forget' },
        { text: 'the' },
        { text: 'hassle', grad: true },
        { text: 'of' },
        { text: 'event' },
        { text: 'planning' },
      ]

  return (
    <section className="relative flex min-h-[calc(100dvh-64px)] items-center overflow-hidden">
      {/* Candy blob backdrop */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="landing-blob landing-blob--a left-[-10%] top-[-12%] h-[55vh] w-[55vw] min-w-[320px]" />
        <div className="landing-blob landing-blob--b right-[-12%] top-[8%] h-[50vh] w-[45vw] min-w-[300px]" />
        <div className="landing-blob landing-blob--c bottom-[-18%] left-[22%] h-[50vh] w-[50vw] min-w-[300px]" />
      </div>

      <div className="relative mx-auto grid w-full max-w-6xl items-center gap-12 px-5 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:gap-8 lg:py-20">
        {/* Copy + CTAs */}
        {/* min-w-0 lets this grid column shrink below its content's min-width —
            without it a long Georgian word forces the track past the viewport
            and the whole column renders trimmed and off-center on mobile. */}
        <div className="min-w-0 text-center lg:text-left">
          {/* Mobile font floor follows the mockup's <=980px rule (9vw): the
              longest Georgian word must fit a 335px content box. Extra leading
              + per-word paint padding keep Georgian ascenders/descenders from
              being clipped by background-clip:text. */}
          <h1 className="font-lynx font-semibold tracking-tight text-balance text-[clamp(2rem,9vw,4.8rem)] sm:text-[clamp(2.7rem,6.5vw,4.8rem)] leading-[1.08]">
            {words.map((w, i) => (
              <Fragment key={`${w.text}-${i}`}>
                <motion.span
                  className={`inline-block max-w-full py-[0.1em] -my-[0.1em]${w.grad ? ' landing-gradient-text' : ''}`}
                  initial={reduce ? false : { opacity: 0, y: '0.55em', rotate: 4 }}
                  animate={{ opacity: 1, y: 0, rotate: 0 }}
                  transition={{ delay: 0.05 + i * 0.09, type: 'spring', stiffness: 240, damping: 20 }}
                >
                  {w.text}
                </motion.span>
                {i < words.length - 1 && ' '}
              </Fragment>
            ))}
          </h1>

          <motion.p
            {...fadeUp(0.5)}
            className="mx-auto mt-5 max-w-[46ch] text-lg leading-relaxed text-celebration-ink-2 dark:text-[#c3b8dd] lg:mx-0"
          >
            {isKa
              ? 'აღარ არის საჭირო რთული პროცესები. შექმენი, გააზიარე და მართე შენი ღონისძიება Storyfy-ის საშუალებით – სწრაფად და მარტივად.'
              : 'No more complicated processes. Create, share and manage your event with Storyfy — fast and simple.'}
          </motion.p>

          <motion.div
            {...fadeUp(0.62)}
            className="mt-9 flex flex-wrap justify-center gap-3 lg:justify-start"
          >
            <Magnetic>
              <Button
                onClick={(e) => {
                  // Snappier, faster burst for the primary hero CTA.
                  burstFromEvent(e, 1.7)
                  navigate('/login')
                }}
                className="group h-12 rounded-full btn-gradient px-6 text-[15px] font-semibold text-white"
                data-testid="home-get-started"
              >
                {isKa ? 'დაიწყე' : 'Get started'}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </Magnetic>
            <Magnetic>
              <Button
                onClick={onSeeExamples}
                variant="outline"
                className="h-12 rounded-full border-transparent bg-white px-6 text-[15px] font-semibold text-celebration-ink shadow-celebrate-sm transition-shadow hover:bg-white hover:shadow-celebrate-md dark:border-white/10 dark:bg-white/10 dark:text-[#f4f0ff] dark:hover:bg-white/15"
              >
                {isKa ? 'ნახე ნიმუშები' : 'See examples'}
              </Button>
            </Magnetic>
          </motion.div>
        </div>

        {/* Floating phone mockup with bobbing bubble tags */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 32, scale: 0.92, rotate: 5 }}
          animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, delay: 0.35, ease: 'easeOut' }}
          className="landing-phone-stage relative mx-auto w-full max-w-[300px]"
        >
          <span
            className="landing-emoji -left-8 top-6 z-10 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-2xl shadow-celebrate-md dark:bg-[#1e1438]"
            style={{ animationDelay: '0.4s' }}
            aria-hidden="true"
          >
            🎉
          </span>
          <span
            className="landing-emoji -right-6 top-24 z-10 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-xl shadow-celebrate-md dark:bg-[#1e1438]"
            style={{ animationDelay: '1.6s' }}
            aria-hidden="true"
          >
            🥂
          </span>
          <span
            className="landing-emoji -left-9 bottom-24 z-10 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-xl shadow-celebrate-md dark:bg-[#1e1438]"
            style={{ animationDelay: '2.4s' }}
            aria-hidden="true"
          >
            ✨
          </span>
          <span
            className="landing-emoji -right-7 bottom-8 z-10 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-2xl shadow-celebrate-md dark:bg-[#1e1438]"
            style={{ animationDelay: '0.9s' }}
            aria-hidden="true"
          >
            🎈
          </span>

          <Tilt max={8}>
            <ShowcaseSlideshow />
          </Tilt>
        </motion.div>
      </div>
    </section>
  )
}

export default Hero
